import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './style/Header.module.scss';
import MenuButton from './components/MenuButton';
import HeaderLogo from './components/HeaderLogo';
import LinkButton from '../../components/Buttons/LinkButton';
import LoginButton from './components/LoginButton';
import moment from 'moment';
import { ReactComponent as Info } from '../../assets/icon/Info.svg';
import { StoreContext } from '../../store/store';
import Notifications from './components/Notifications';
import ShoppingCartButton from './components/ShoppingCartButton';
import Api, { initApis } from '../../api/Api';
import { useUserProvider } from '../../generic/hooks';
import { DatePFormat } from '../../generic/StaticVariable';
import SearchBar from './components/SearchBar';
import { withLoading } from '../../components/Loading/withLoading';
import LoadingContext from '../../context/loadingContext/LoadingContext';

type PrivateProps = {
  advertisingBannerClick: () => void;
};

export default function Header({ advertisingBannerClick }: PrivateProps) {
  const {
    setShowSubscriptionExpiredModal,
    setUser,
    setShoppingItems,
    showPricingModal,
    showContactModal,
    setShowCart,
    setSearchId,
    showCart,
    setShowLogin,
    showLogin,
    showDownloadApiModal,
    advertisingBannerButtonText,
  } = useContext(StoreContext);
  const loadingContext = useContext(LoadingContext)

  const navigate = useNavigate();
  const [isAuthorized] = useUserProvider();

  useEffect(() => {
    withLoading(() => tryToLoginSilently(), loadingContext, {
      source: 'Header - login silent',
    });
  }, []);

  const tryToLoginSilently = React.useCallback(async () => {
    try {
      await initApis();
      const user = await Api.currentUser.getCurrentUser();

      if (!user) {
        throw new Error('User retrieval failed');
      }

      setUser(user);

      // Init user tracking in Matomo
      const _paq = (window._paq = window._paq || []);
      _paq.push(['setUserId', user.userName]);
      _paq.push(['trackPageView']);
      _paq.push(['enableHeartBeatTimer']);

      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

      if (user?.subscriptionExpiryDate) {
        const subscriptionExpiryDateObj = new Date(user.subscriptionExpiryDate);
        const formattedSubscriptionDate = moment(subscriptionExpiryDateObj).format(DatePFormat);
        if (now > subscriptionExpiryDateObj) {
          setShowSubscriptionExpiredModal({
            header: 'Subscription expired',
            message: `Your subscription to ${user.subscriptionType} expired on ${formattedSubscriptionDate}.
                      Please contact customer care mike@dhigroup.com to renew your subscription`,
            status: true
          });
        } else if (subscriptionExpiryDateObj <= threeMonthsFromNow) {
          setShowSubscriptionExpiredModal({
            header: 'Subscription expiry',
            message: `Your subscription to ${user.subscriptionType} expires on ${formattedSubscriptionDate}.
                      Please contact customer care mike@dhigroup.com to extend your subscription`,
            status: true
          });
        }
      }
    } catch (error) {
      console.log('Error during login:', error);
      setUser({ authenticated: false });
    }
  }, []);

  useEffect(() => {

    const getShoppingCart = async () => {
      const data = await Api.shopping.GetShoppingCart();
      setShoppingItems(data.shoppingItems);
    }

    if (isAuthorized) {
      getShoppingCart();
    }
  }, [isAuthorized]);

  const handleCart = () => {
    if (isAuthorized) {
      setShowCart(!showCart)
    } else {
      setShowLogin(!showLogin)
    }
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.left}>
          <HeaderLogo
            onClick={() => {
              navigate('/');
            }}
          />

          <MenuButton
            onClick={() => {
              navigate('/features');
            }}
            title={'GET STARTED'}
            active={false}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              navigate('pricing');
            }}
            title={'Pricing'}
            active={showPricingModal}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              navigate('/contact');
            }}
            title={'Contact'}
            active={showContactModal}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              navigate('/api');
            }}
            title={'Api'}
            active={showDownloadApiModal}
          ></MenuButton>
        </div>
        <div className={styles.right}>
          <div className={styles.linkButtonContainer}>
            {localStorage.getItem('showAdvertisingBanner') === 'true' && (
              <LinkButton
                onClick={() => advertisingBannerClick()}
                title={advertisingBannerButtonText || 'Learn more'}
              >
                <Info className={styles.svg} />
              </LinkButton>
            )}
            <SearchBar
              searchID={(id) => setSearchId(id)}
            />
          </div>

          <div className={styles.iconsContainer}>
            <LinkButton smallHide={true} onClick={handleCart}>
              <ShoppingCartButton />
            </LinkButton>
            <LinkButton
              smallHide={true}
              onClick={() => {
                navigate('/notifications');
              }}            >
              <Notifications />
            </LinkButton>
          </div>

          <LoginButton />
        </div>
      </header>
    </>
  );
}
